import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  LayoutDashboard, Inbox, CalendarClock, Axe, Images, ImagePlus, Star, Newspaper,
  Settings as SettingsIcon, CalendarX, Users as UsersIcon, DatabaseBackup, LogOut,
  Menu, X, Contact2, BarChart3, Bell, Search, KeyRound, Palette, HelpCircle, ChevronRight,
  Globe, ShieldCheck, ExternalLink,
} from "lucide-react";
import { useStudio } from "./StudioContext";
import CrudPanel from "../admin/CrudPanel";
import {
  RequestsPanel, BookingsPanel, SettingsPanel, UsersPanel, CalendarPanel, BackupPanel,
} from "../admin/panels";
import DashboardHome from "../admin/DashboardHome";
import {
  CustomersPanel, NotificationsPanel, SeoPanel, ReportsPanel, ChangePasswordPanel,
  BrandingPanel, HelpCenterPanel,
} from "../admin/morepanels";
import ClientSitesPanel from "./ClientSitesPanel";
import { T } from "./theme";

const SERVICE_FIELDS = [{ key: "title", label: "Title" }, { key: "desc", label: "Description", type: "textarea" }, { key: "icon", label: "Icon", type: "icon" }, { key: "order", label: "Order", type: "number" }];
const PROJECT_FIELDS = [{ key: "title", label: "Title" }, { key: "category", label: "Category" }, { key: "before", label: "Before image", type: "image" }, { key: "after", label: "After image", type: "image" }, { key: "order", label: "Order", type: "number" }];
const GALLERY_FIELDS = [{ key: "tag", label: "Tag / Caption" }, { key: "src", label: "Image or video (mp4)", type: "image" }, { key: "poster", label: "Video poster (optional)", type: "image" }, { key: "order", label: "Order", type: "number" }];
const TESTIMONIAL_FIELDS = [{ key: "name", label: "Customer name" }, { key: "text", label: "Review", type: "textarea" }, { key: "rating", label: "Rating (1-5)", type: "number" }, { key: "date", label: "Date label" }, { key: "src", label: "Source" }, { key: "order", label: "Order", type: "number" }];
const BLOG_FIELDS = [{ key: "title", label: "Title" }, { key: "slug", label: "URL slug" }, { key: "excerpt", label: "Excerpt", type: "textarea" }, { key: "meta_description", label: "SEO meta description", type: "textarea" }, { key: "cover", label: "Cover image", type: "image" }, { key: "body", label: "Body (HTML)", type: "textarea", rows: 12 }, { key: "published", label: "Published", type: "checkbox" }, { key: "order", label: "Order", type: "number" }];

function YosiBrand() {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className="inline-flex items-center justify-center w-10 h-10 rounded-xl font-black text-lg"
        style={{ background: `linear-gradient(135deg, ${T.gold}, ${T.goldHi})`, color: "#0A0A0C" }}
      >
        Y
      </span>
      <div className="leading-tight">
        <div className="font-display font-extrabold text-base tracking-tight" style={{ color: T.text }}>
          Yosi Media Studio
        </div>
        <div className="text-[9px] tracking-[0.22em] uppercase" style={{ color: T.gold }}>
          Agency Portal
        </div>
      </div>
    </div>
  );
}

export default function StudioDashboard() {
  const { user, logout } = useStudio();
  const [open, setOpen] = useState(false);

  const TABS = useMemo(() => [
    { id: "dashboard", label: "Overview", icon: LayoutDashboard, el: <DashboardHome user={user} isClient={false} /> },
    { id: "sites", label: "Client Websites", icon: Globe, el: <ClientSitesPanel /> },
    { id: "requests", label: "All Estimate Requests", icon: Inbox, el: <RequestsPanel /> },
    { id: "appointments", label: "All Appointments", icon: CalendarClock, el: <BookingsPanel /> },
    { id: "customers", label: "Customers", icon: Contact2, el: <CustomersPanel /> },
    { id: "calendar", label: "Availability Calendar", icon: CalendarX, el: <CalendarPanel /> },
    { id: "services", label: "Services", icon: Axe, el: <CrudPanel coll="services" title="Services" fields={SERVICE_FIELDS} labelKey="title" /> },
    { id: "projects", label: "Before / After", icon: ImagePlus, el: <CrudPanel coll="projects" title="Before / After" fields={PROJECT_FIELDS} labelKey="title" /> },
    { id: "gallery", label: "Gallery", icon: Images, el: <CrudPanel coll="gallery" title="Gallery" fields={GALLERY_FIELDS} labelKey="tag" /> },
    { id: "testimonials", label: "Testimonials", icon: Star, el: <CrudPanel coll="testimonials" title="Testimonials" fields={TESTIMONIAL_FIELDS} labelKey="name" /> },
    { id: "blog", label: "Blog", icon: Newspaper, el: <CrudPanel coll="blog" title="Blog Posts" fields={BLOG_FIELDS} labelKey="title" /> },
    { id: "reports", label: "Reports", icon: BarChart3, el: <ReportsPanel /> },
    { id: "notifications", label: "Notifications", icon: Bell, el: <NotificationsPanel /> },
    { id: "seo", label: "SEO", icon: Search, el: <SeoPanel /> },
    { id: "settings", label: "Business Info", icon: SettingsIcon, el: <SettingsPanel /> },
    { id: "branding", label: "Branding", icon: Palette, el: <BrandingPanel /> },
    { id: "users", label: "Team & Roles", icon: UsersIcon, el: <UsersPanel me="super_admin" /> },
    { id: "backup", label: "Backup & Restore", icon: DatabaseBackup, el: <BackupPanel /> },
    { id: "changepw", label: "Change Password", icon: KeyRound, el: <ChangePasswordPanel /> },
    { id: "help", label: "Help Center", icon: HelpCircle, el: <HelpCenterPanel /> },
  ], [user]);

  const GROUPS = [
    { title: "Agency", ids: ["dashboard", "sites"] },
    { title: "Operations", ids: ["requests", "appointments", "customers", "calendar"] },
    { title: "Content", ids: ["services", "projects", "gallery", "testimonials", "blog"] },
    { title: "Growth", ids: ["reports", "notifications", "seo"] },
    { title: "Configuration", ids: ["settings", "branding", "users", "backup"] },
    { title: "Account", ids: ["changepw", "help"] },
  ];

  const [active, setActive] = useState("dashboard");
  const current = TABS.find((t) => t.id === active) || TABS[0];

  const NavButton = ({ t }) => {
    const Icon = t.icon;
    const isActive = active === t.id;
    return (
      <button data-testid={`studio-tab-${t.id}`} onClick={() => { setActive(t.id); setOpen(false); }}
        title={t.label}
        className="group w-full flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-all"
        style={{
          color: isActive ? "#0A0A0C" : T.textDim,
          background: isActive ? `linear-gradient(135deg, ${T.gold}, ${T.goldHi})` : "transparent",
          fontWeight: isActive ? 600 : 500,
        }}
        onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.background = "rgba(255,255,255,0.04)"; }}
        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = "transparent"; }}>
        <Icon size={16} strokeWidth={isActive ? 2.4 : 2} />
        <span className="truncate flex-1 text-left">{t.label}</span>
        {isActive && <ChevronRight size={14} />}
      </button>
    );
  };

  const SideContent = () => (
    <>
      <div className="px-4 py-4 border-b flex items-center justify-between" style={{ borderColor: T.border }}>
        <YosiBrand />
      </div>
      <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-4">
        {GROUPS.map((g) => (
          <div key={g.title}>
            <div className="px-3 pb-1.5 text-[10px] font-bold tracking-[0.18em] uppercase" style={{ color: T.textFaint }}>{g.title}</div>
            <div className="space-y-0.5">
              {TABS.filter((t) => g.ids.includes(t.id)).map((t) => <NavButton key={t.id} t={t} />)}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t space-y-1" style={{ borderColor: T.border }}>
        <div className="px-3 py-2 mb-1 rounded-lg" style={{ background: T.card }}>
          <div className="text-xs font-semibold truncate" style={{ color: T.text }}>{user?.name || user?.email}</div>
          <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: T.gold }}>Super Admin</div>
        </div>
        <a href="/admin" target="_blank" rel="noreferrer"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5"
          style={{ color: T.textDim }}>
          <ShieldCheck size={15} /> Client portal
        </a>
        <Link to="/" target="_blank"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5"
          style={{ color: T.textDim }}>
          <ExternalLink size={15} /> View website
        </Link>
        <button onClick={logout} data-testid="studio-logout"
          className="w-full flex items-center gap-2 px-3 py-2 text-sm rounded-lg hover:bg-white/5"
          style={{ color: T.textDim }}>
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ background: T.bg, color: T.text }}>
      <Helmet>
        <title>Yosi Media Studio | Agency Portal</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <aside className="hidden lg:flex w-[260px] flex-col fixed inset-y-0 border-r"
        style={{ background: T.side, borderColor: T.border }}>
        <SideContent />
      </aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <aside className="w-72 flex flex-col border-r" style={{ background: T.side, borderColor: T.border }}>
            <SideContent />
          </aside>
          <div className="flex-1" style={{ background: "rgba(0,0,0,0.6)" }} onClick={() => setOpen(false)} />
        </div>
      )}
      <main className="flex-1 lg:ml-[260px] min-w-0">
        <header className="lg:hidden flex items-center justify-between px-4 h-14 sticky top-0 z-40 border-b"
          style={{ background: T.side, borderColor: T.border }}>
          <YosiBrand />
          <button onClick={() => setOpen(!open)} data-testid="studio-mobile-menu" style={{ color: T.gold }}>
            {open ? <X /> : <Menu />}
          </button>
        </header>
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto">{current?.el}</div>
      </main>
    </div>
  );
}
